import _ from 'lodash'
import slugify from 'slugify'
import Schema from 'schema-dts'
import { JsonLd } from 'react-schemaorg'
import { Vehicle } from '@cruz-com/lookup'
import { webBaseUrl, pages } from '@/settings'

type Props = {
  name: string
  imageUrl?: string
  vehicleList: Vehicle.Summary[]
}

const lkpCondition = {
  new: 'NewCondition',
  used: 'UsedCondition',
  certified: 'UsedCondition',
  repairable: 'DamagedCondition'
}

const lkpDriveType = {
  AWD: 'AllWheelDriveConfiguration',
  FWD: 'FrontWheelDriveConfiguration',
  '4X4': 'FourWheelDriveConfiguration',
  '4X2': 'FourWheelDriveConfiguration',
  RWD: 'RearWheelDriveConfiguration'
}

export const VehicleListSchema = ({ name, imageUrl, vehicleList }: Props) => {
  try {
    if (_.isEmpty(vehicleList)) {
      return <></>
    }

    const inventoryItems: Schema.ListItem[] = _.map(vehicleList, (v, idx) => {

      const { _id, condition, sale_status_id, make, model, year, trim, vin, mileage, reference, location, photo_preview_url, engine } = v || {}
      const { city, state_abbr, zip_code, address1 } = location || {}
      const title = _.compact([year, make, model, trim]).join(' ')

      const url = webBaseUrl + pages.vehicleDetail.href
        .replace('[location]', slugify([city, state_abbr, zip_code].join(' ')))
        .replace('[title]', slugify(title)) + '/' + _id

      const hasOffer = (sale_status_id === Vehicle.SaleStatusEnum.ForSale
        || sale_status_id === Vehicle.SaleStatusEnum.OpenToOffers
        || sale_status_id !== Vehicle.SaleStatusEnum.Sold)

      const seller: Schema.Corporation | Schema.Person = location?.name
        ? {
          '@type': 'Corporation',
          name: location.name,
          telephone: location.phone,
          address: {
            '@type': 'PostalAddress',
            addressLocality: city,
            addressRegion: state_abbr,
            postalCode: zip_code,
            streetAddress: address1,
            addressCountry: 'US'
          }
        }
        : {
          '@type': 'Person',
          name: ''
        }

      const d: Schema.Vehicle & { offers?: Schema.Offer } = {
        '@type': 'Vehicle',
        name: title,
        vehicleModelDate: year?.toString(),
        brand: {
          '@type': 'Brand',
          name: make
        },
        model: model,
        vehicleIdentificationNumber: vin || '',
        image: photo_preview_url || '',
        url,
        mileageFromOdometer: {
          '@type': 'QuantitativeValue',
          value: mileage || 1,
          unitText: 'mile',
          unitCode: 'SMI'
        },
        sku: reference?.stock_number || _id,
        itemCondition: _.get(lkpCondition, condition as any) || 'UsedCondition',
        fuelType: engine?.fuel_type || '',
      }

      if (v?.drive_type) {
        const x = _.get(lkpDriveType, v.drive_type)
        if (x) {
          d.driveWheelConfiguration = x
        }
      }

      if ((v?.price || 0) > 0) {
        d.offers = {
          '@type': 'Offer',
          price: v.price_sort_lh,
          priceCurrency: 'USD',
          url,
          availability: hasOffer ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          itemCondition: _.get(lkpCondition, condition as any) || 'https://schema.org/UsedCondition',
          seller: seller
        }
      }

      const item: Schema.ListItem = {
        '@type': 'ListItem',
        position: idx + 1,
        url,
        item: d
      }

      return item
    })

    const data: Schema.ItemList & { "@context": "https://schema.org" } = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name,
      itemListElement: inventoryItems,
    }

    if (imageUrl) {
      data.image = webBaseUrl + imageUrl
    }

    return (
      <JsonLd<Schema.ItemList> item={data} />
    )
  } catch {
    return <></>
  }
}